import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import './Login.css'
import Cookies from 'js-cookie'
import api from '../config/api'
import { persistAuth, getDashboardForRole } from '../config/authUtils'

const Login = () => {
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false)
    const [forgotPasswordEmail, setForgotPasswordEmail] = useState('')
    const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false)
    const [forgotPasswordMessage, setForgotPasswordMessage] = useState('')
    const navigate = useNavigate()

    const handleSubmit = async (e) => {
        e.preventDefault()
        setError('')
        setLoading(true)

        try {
            const response = await api.post('/api/login/', {
                email,
                password,
            })

            if (response.data?.access) {
                Cookies.set('token', response.data.access)
            } else if (response.data?.token) {
                Cookies.set('token', response.data.token)
            }

            if (response.data?.user?.role) {
                Cookies.set('role', response.data.user.role)
            }

            persistAuth(response.data)

            const redirect = getDashboardForRole(response.data?.user?.role)
            navigate(redirect || '/login')
        } catch (err) {
            console.log(err)
            const errorMsg =
                err.response?.data?.non_field_errors?.[0] ||
                err.response?.data?.detail ||
                err.response?.data?.message ||
                'An error occurred during login'
            setError(errorMsg)
        } finally {
            setLoading(false)
        }
    }

    const togglePasswordVisibility = () => {
        setShowPassword((prev) => !prev)
    }

    const handleForgotPassword = async (e) => {
        e.preventDefault()
        setForgotPasswordMessage('')
        setForgotPasswordLoading(true)

        try {
            await api.post('/forgot-password/', {
                email: forgotPasswordEmail,
            })

            setForgotPasswordMessage(
                'Password reset instructions have been sent to your email.'
            )
            setForgotPasswordEmail('')

            setTimeout(() => {
                setShowForgotPasswordModal(false)
                setForgotPasswordMessage('')
            }, 3000)
        } catch (err) {
            console.log(err)
            const errorMsg =
                err.response?.data?.non_field_errors?.[0] ||
                err.response?.data?.detail ||
                err.response?.data?.message ||
                'An error occurred. Please try again.'
            setForgotPasswordMessage(errorMsg)
        } finally {
            setForgotPasswordLoading(false)
        }
    }

    const openForgotPasswordModal = () => {
        setShowForgotPasswordModal(true)
        setForgotPasswordEmail('')
        setForgotPasswordMessage('')
    }

    const closeForgotPasswordModal = () => {
        setShowForgotPasswordModal(false)
        setForgotPasswordEmail('')
        setForgotPasswordMessage('')
    }

    const isForgotError =
        forgotPasswordMessage &&
        (forgotPasswordMessage.includes('error') ||
            forgotPasswordMessage.includes('An error'))

    return (
        <div className="login-page">
            <div className="background-shapes">
                <div className="shape shape-1"></div>
                <div className="shape shape-2"></div>
                <div className="shape shape-4"></div>
                <div className="shape shape-5"></div>
                <div className="shape shape-6"></div>
                <div className="shape shape-7"></div>
                <div className="shape shape-8"></div>
                <div className="shape shape-9"></div>
            </div>

            <div className="login-container">
                <div className="logo-container mb-5">
                    <img
                        src="assets/img/media/celebration-logo.png"
                        alt="Company Logo"
                        className="company-logo"
                        onError={(e) => {
                            e.target.style.display = 'none'
                            const logoText =
                                document.querySelector('.logo-text')
                            if (logoText) logoText.style.display = 'block'
                        }}
                    />
                    <h2 className="logo-text" style={{ display: 'none' }}>
                        PMS
                    </h2>
                </div>

                <form onSubmit={handleSubmit} className="login-form">
                    {error && (
                        <div
                            className="alert alert-danger border-0 mb-4 shadow-sm fs-14"
                            role="alert"
                        >
                            <div className="d-flex align-items-center gap-2">
                                <i className="ti ti-alert-triangle fs-18 flex-shrink-0" />
                                <span>{error}</span>
                            </div>
                        </div>
                    )}

                    <div className="form-group mb-3">
                        <label
                            htmlFor="email"
                            className="form-label fs-13 fw-medium mb-2"
                            style={{ color: 'rgba(255,255,255,0.85)' }}
                        >
                            Email
                        </label>
                        <div className="input-container position-relative">
                            <span
                                className="position-absolute top-50 translate-middle-y d-flex align-items-center justify-content-center"
                                style={{ left: '14px', color: 'rgba(255,255,255,0.55)' }}
                            >
                                <i className="ti ti-mail fs-18" />
                            </span>
                            <input
                                type="email"
                                id="email"
                                className="form-control form-control-lg fs-14"
                                style={{
                                    paddingLeft: '46px',
                                    backgroundColor: 'rgba(255,255,255,0.07)',
                                    border: '1px solid rgba(255,255,255,0.12)',
                                    color: '#fff',
                                    borderRadius: '10px',
                                }}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter your email"
                                autoComplete="email"
                                required
                            />
                        </div>
                    </div>

                    <div className="form-group mb-3" style={{ marginTop: '20px' }}>
                        <label
                            htmlFor="password"
                            className="form-label fs-13 fw-medium mb-2"
                            style={{ color: 'rgba(255,255,255,0.85)' }}
                        >
                            Password
                        </label>
                        <div className="input-container position-relative">
                            <span
                                className="position-absolute top-50 translate-middle-y d-flex align-items-center justify-content-center"
                                style={{ left: '14px', color: 'rgba(255,255,255,0.55)' }}
                            >
                                <i className="ti ti-lock fs-18" />
                            </span>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                className="form-control form-control-lg fs-14"
                                style={{
                                    paddingLeft: '46px',
                                    paddingRight: '48px',
                                    backgroundColor: 'rgba(255,255,255,0.07)',
                                    border: '1px solid rgba(255,255,255,0.12)',
                                    color: '#fff',
                                    borderRadius: '10px',
                                }}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter your password"
                                autoComplete="current-password"
                                required
                            />
                            <button
                                type="button"
                                className="btn btn-link position-absolute top-50 end-0 translate-middle-y text-decoration-none p-0 me-3 d-flex align-items-center justify-content-center"
                                onClick={togglePasswordVisibility}
                                style={{ color: 'rgba(255,255,255,0.6)' }}
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                            >
                                <i
                                    className={`ti ${
                                        showPassword ? 'ti-eye-off' : 'ti-eye'
                                    } fs-18`}
                                />
                            </button>
                        </div>
                    </div>

                    <div
                        className="text-end"
                        style={{ marginTop: '18px' }}
                    >
                        <button
                            type="button"
                            className="btn btn-link p-0 text-decoration-none fs-13 fw-medium"
                            onClick={openForgotPasswordModal}
                            style={{ color: '#a5b4fc' }}
                        >
                            Forgot Password?
                        </button>
                    </div>

                    <button
                        type="submit"
                        className="btn w-100 py-3 fw-bold fs-15 shadow-lg"
                        disabled={loading}
                        style={{
                            marginTop: '25px',
                            background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',
                            border: 'none',
                            color: '#fff',
                            borderRadius: '10px',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        }}
                        onMouseOver={(e) => {
                            if (!loading) {
                                e.currentTarget.style.transform = 'translateY(-1px)'
                                e.currentTarget.style.boxShadow =
                                    '0 10px 24px rgba(79, 70, 229, 0.35)'
                            }
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)'
                            e.currentTarget.style.boxShadow =
                                '0 8px 16px rgba(0, 0, 0, 0.15)'
                        }}
                    >
                        {loading ? (
                            <span className="d-flex align-items-center justify-content-center gap-2">
                                <span
                                    className="spinner-border spinner-border-sm text-white"
                                    role="status"
                                    aria-hidden="true"
                                />
                                <span>Signing in...</span>
                            </span>
                        ) : (
                            <span className="d-flex align-items-center justify-content-center gap-2">
                                <i className="ti ti-login-2 fs-18" />
                                Login
                            </span>
                        )}
                    </button>
                </form>
            </div>

            {showForgotPasswordModal && (
                <div
                    className="modal fade show d-block"
                    tabIndex="-1"
                    role="dialog"
                    style={{ backgroundColor: 'rgba(15, 23, 42, 0.65)' }}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) closeForgotPasswordModal()
                    }}
                >
                    <div
                        className="modal-dialog modal-dialog-centered"
                        role="document"
                        style={{ maxWidth: '420px' }}
                    >
                        <div className="modal-content shadow-lg border-0 rounded-4 overflow-hidden">
                            <div
                                className="px-4 py-4 border-bottom d-flex align-items-center justify-content-between"
                                style={{
                                    background:
                                        'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',
                                }}
                            >
                                <div className="d-flex align-items-center gap-2 text-white">
                                    <i className="ti ti-lock-open fs-22" />
                                    <h5 className="mb-0 fw-bold fs-17">
                                        Forgot Password
                                    </h5>
                                </div>
                                <button
                                    type="button"
                                    className="btn p-0 border-0 d-flex align-items-center justify-content-center text-white"
                                    style={{
                                        opacity: 0.85,
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '8px',
                                    }}
                                    onClick={closeForgotPasswordModal}
                                    onMouseOver={(e) =>
                                        (e.currentTarget.style.backgroundColor =
                                            'rgba(255,255,255,0.15)')
                                    }
                                    onMouseOut={(e) =>
                                        (e.currentTarget.style.backgroundColor =
                                            'transparent')
                                    }
                                    aria-label="Close"
                                >
                                    <i className="ti ti-x fs-20" />
                                </button>
                            </div>

                            <div className="p-4">
                                <p className="fs-14 text-muted mb-4" style={{ lineHeight: 1.6 }}>
                                    Enter your email address and we&apos;ll send you instructions to
                                    reset your password.
                                </p>

                                <form onSubmit={handleForgotPassword}>
                                    <div className="mb-3">
                                        <label
                                            htmlFor="forgot-email"
                                            className="form-label fs-13 fw-medium text-dark"
                                        >
                                            Email
                                        </label>
                                        <div className="position-relative">
                                            <span
                                                className="position-absolute top-50 translate-middle-y d-flex align-items-center justify-content-center text-muted"
                                                style={{ left: '14px' }}
                                            >
                                                <i className="ti ti-mail fs-18" />
                                            </span>
                                            <input
                                                type="email"
                                                id="forgot-email"
                                                className="form-control form-control-lg fs-14"
                                                style={{
                                                    paddingLeft: '46px',
                                                    borderRadius: '10px',
                                                    borderColor: '#e2e8f0',
                                                }}
                                                value={forgotPasswordEmail}
                                                onChange={(e) =>
                                                    setForgotPasswordEmail(e.target.value)
                                                }
                                                placeholder="Enter your email"
                                                required
                                            />
                                        </div>
                                    </div>

                                    {forgotPasswordMessage && (
                                        <div
                                            className={`alert fs-13 border-0 py-2 px-3 mb-3 d-flex align-items-center gap-2 ${
                                                isForgotError
                                                    ? 'alert-danger'
                                                    : 'alert-success'
                                            }`}
                                            role="alert"
                                        >
                                            <i
                                                className={`ti fs-18 flex-shrink-0 ${
                                                    isForgotError
                                                        ? 'ti-alert-triangle'
                                                        : 'ti-circle-check'
                                                }`}
                                            />
                                            <span>{forgotPasswordMessage}</span>
                                        </div>
                                    )}

                                    <div className="d-flex gap-2 mt-4">
                                        <button
                                            type="button"
                                            className="btn btn-lg flex-grow-1 fs-14 fw-medium border"
                                            style={{
                                                borderRadius: '10px',
                                                borderColor: '#e2e8f0',
                                                backgroundColor: '#fff',
                                                color: '#475569',
                                            }}
                                            onClick={closeForgotPasswordModal}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-lg flex-grow-1 fs-14 fw-bold text-white shadow-sm"
                                            disabled={forgotPasswordLoading}
                                            style={{
                                                borderRadius: '10px',
                                                background:
                                                    'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',
                                                border: 'none',
                                                opacity: forgotPasswordLoading ? 0.7 : 1,
                                            }}
                                        >
                                            {forgotPasswordLoading ? (
                                                <span className="d-flex align-items-center justify-content-center gap-2">
                                                    <span
                                                        className="spinner-border spinner-border-sm text-white"
                                                        role="status"
                                                        aria-hidden="true"
                                                    />
                                                    <span>Sending...</span>
                                                </span>
                                            ) : (
                                                <span className="d-flex align-items-center justify-content-center gap-2">
                                                    <i className="ti ti-send fs-16" />
                                                    Send Instructions
                                                </span>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default Login
